import RBush from 'rbush';
import type { Id } from '@vp/shared-types';
import type { Rect } from '@vp/core';

interface Entry {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  id: Id;
}

/**
 * Incremental R-tree over world-space bounds (float64) (§11.4).
 * Bulk loads on large inserts, single inserts/removals otherwise.
 */
export class SpatialIndex {
  private tree = new RBush<Entry>(16);
  private entries = new Map<Id, Entry>();

  get size(): number {
    return this.entries.size;
  }

  clear(): void {
    this.tree.clear();
    this.entries.clear();
  }

  load(items: Iterable<{ id: Id; rect: Rect }>): void {
    const batch: Entry[] = [];
    for (const { id, rect } of items) {
      const old = this.entries.get(id);
      if (old) this.tree.remove(old);
      const e = toEntry(id, rect);
      this.entries.set(id, e);
      batch.push(e);
    }
    this.tree.load(batch);
  }

  upsert(id: Id, rect: Rect): void {
    const old = this.entries.get(id);
    if (old) {
      if (old.minX === rect.x && old.minY === rect.y && old.maxX === rect.x + rect.w && old.maxY === rect.y + rect.h) return;
      this.tree.remove(old);
    }
    const e = toEntry(id, rect);
    this.entries.set(id, e);
    this.tree.insert(e);
  }

  remove(id: Id): void {
    const old = this.entries.get(id);
    if (!old) return;
    this.tree.remove(old);
    this.entries.delete(id);
  }

  search(r: Rect): Id[] {
    return this.tree.search({ minX: r.x, minY: r.y, maxX: r.x + r.w, maxY: r.y + r.h }).map((e) => e.id);
  }

  bounds(id: Id): Rect | null {
    const e = this.entries.get(id);
    return e ? { x: e.minX, y: e.minY, w: e.maxX - e.minX, h: e.maxY - e.minY } : null;
  }

  /** Union of all entries (for "fit all"). */
  totalBounds(): Rect | null {
    if (this.entries.size === 0) return null;
    const d = this.tree.toJSON() as { minX: number; minY: number; maxX: number; maxY: number };
    return { x: d.minX, y: d.minY, w: d.maxX - d.minX, h: d.maxY - d.minY };
  }

  stats(): { entries: number; height: number } {
    const d = this.tree.toJSON() as { height: number };
    return { entries: this.entries.size, height: d.height ?? 0 };
  }
}

function toEntry(id: Id, r: Rect): Entry {
  return { minX: r.x, minY: r.y, maxX: r.x + r.w, maxY: r.y + r.h, id };
}
